import { isSensitivePath } from '@/lib/privacy';
import React, { useEffect, useRef, useState } from 'react';

import { captureLeadAttribution } from '@/lib/leadTracking';
import { MARKETING_CONSENT_KEY } from '@/lib/marketingConsent';

const CONSENT_KEY = 'tda_cookie_consent_v2';
const AD_CONSENT_KEY = MARKETING_CONSENT_KEY;

function applyConsent() {
  window.dispatchEvent(new Event('tda-consent-change'));
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [preferenceRequest, setPreferenceRequest] = useState(0);
  const noticeRef = useRef(null);

  useEffect(() => {
    const openPreferences = () => {
      setVisible(true);
      setPreferenceRequest(count => count + 1);
    };
    window.addEventListener('tda-cookie-preferences', openPreferences);
    const cleanup = () => { window.removeEventListener('tda-cookie-preferences', openPreferences); };
    let stored; try { stored = localStorage.getItem(AD_CONSENT_KEY); } catch {}
    if (isSensitivePath() && !/^\/consult\/?$/i.test(window.location.pathname)) return cleanup;
    if (!stored) {
      // Present the choice with the first render cycle, rather than adding a late LCP candidate.
      setVisible(true);
      return cleanup;
    }
    // Re-apply stored consent on every page load
    applyConsent();
    return cleanup;
  }, []);

  useEffect(() => {
    if (!visible || preferenceRequest === 0) return;
    const notice = noticeRef.current;
    if (!notice) return;
    if (window.matchMedia('(max-width: 767px)').matches) {
      notice.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
    notice.focus({ preventScroll: true });
  }, [visible, preferenceRequest]);

  const handleAccept = () => {
    try { localStorage.setItem(CONSENT_KEY, 'granted'); localStorage.setItem(AD_CONSENT_KEY, 'granted'); } catch { return; }
    applyConsent();
    captureLeadAttribution();
    setVisible(false);
  };

  const handleDecline = () => {
    try { localStorage.setItem(CONSENT_KEY, 'denied'); localStorage.setItem(AD_CONSENT_KEY, 'denied'); } catch { /* Browser already blocks storage. */ }
    applyConsent();
    captureLeadAttribution();
    setVisible(false);
    window.location.reload();
  };

  if (!visible) return null;

  return (
    <div
      ref={noticeRef}
      tabIndex={-1}
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
      className="relative md:fixed md:bottom-0 md:inset-x-0 z-[9999] max-w-full bg-white border-t border-slate-200 shadow-2xl"
    >
      <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-slate-700 leading-snug">
            Google measurement is on by default. <strong>Decline</strong> turns off nonessential measurement;
            <strong> Accept</strong> also enables the advertising tools listed below.
          </p>
          <details className="mt-1 text-xs text-slate-600">
            <summary className="cursor-pointer underline underline-offset-2 py-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-700">Cookie details &amp; privacy</summary>
            <div className="max-h-[35dvh] overflow-y-auto overscroll-contain pr-2 py-2">
        <p className="text-sm text-slate-600 leading-relaxed">
          Google Analytics and Google Ads measurement are enabled by default on eligible public pages under our practice’s consent process. Select <strong>Decline</strong> to turn off nonessential measurement. Select <strong>Accept</strong> to also allow OpenAI, Vibe, RetargetIQ and GrowDental advertising tools. These tools use cookies and other identifiers; RetargetIQ may recognize visitors through matched identifiers. Restricted patient pages are excluded. Change your choice using Cookie Preferences in the footer.
          See our{' '}
          <a href="/privacy-policy" className="underline text-slate-800 hover:text-cyan-700">
            Privacy Policy
          </a>
          .
        </p>
            </div>
          </details>
        </div>
        <div className="flex gap-3 flex-shrink-0 sm:self-center">
          <button
            onClick={handleDecline}
            className="min-h-11 flex-1 sm:flex-none px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
          >
            Decline
          </button>
          <button
            onClick={handleAccept}
            className="min-h-11 flex-1 sm:flex-none px-5 py-2 text-sm font-medium bg-cyan-700 text-white rounded-lg hover:bg-cyan-800 transition-colors"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
